/**
 * CarouselRow — a horizontally-scrolling row with left/right scroll arrows that fade in only when
 * there is more content to reveal in that direction, and scroll one item at a time (smooth). This is
 * the same affordance the chat `ImageGalleryWidget` uses for its inline image carousel, lifted into a
 * content-agnostic wrapper so card rows and image rows share one behaviour.
 *
 * Children should be fixed-width and `shrink-0` (e.g. `className="w-72 shrink-0"`); the per-item scroll
 * stride is measured from the first two children, so any gap is honoured automatically.
 *
 * The arrows come in two weights. `button` (the default) is a solid chip with a border and a shadow —
 * right for the stories rails, where the arrows sit BESIDE cards on a light page and have to hold their
 * own against it.
 *
 * `header` is the odd one out structurally: the arrows leave the row entirely and sit in a bar ABOVE
 * it. Overlaid arrows work when they sit on a photo, but a row of whole cards has no such surface —
 * they would cover a title or a price, and an arrow that materialises only once you have already
 * scrolled does not advertise that there is more to see. So within a row that scrolls, both arrows
 * stay put and DISABLE at their end rather than vanishing. A row whose content fits, though, drops
 * them entirely: a permanently dead pair of controls reads as broken, not as a limit. The bar also
 * gives a caller somewhere to put a label, via `header`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

export interface CarouselRowProps {
  children: React.ReactNode;
  /** Classes for the outer relative wrapper. */
  className?: string;
  /** Classes for the inner horizontal scroll container (gap, padding, snap behaviour, etc.). */
  contentClassName?: string;
  /** Describes the row for the arrow buttons' accessible labels (e.g. "images"). */
  label?: string;
  /**
   * Tailwind `from-*` colour the edge fade blends into — set this to the surface the row sits on so the
   * overflowing items appear to dissolve into the page. Defaults to `from-background`.
   */
  fadeFrom?: string;
  /** Arrow chrome: `button` beside content on a page, `header` in a bar above it. Defaults to `button`. */
  arrows?: 'button' | 'header';
  /**
   * Leading content for the `header` bar — a label or count, sat opposite the arrows. Shown only when
   * the row actually scrolls, because the bar it lives in is the scroll affordance.
   */
  header?: React.ReactNode;
  /**
   * Keep `header` when nothing overflows, with only the arrows hidden: for a header that says something the
   * cards don't (the hotel a carousel of rooms is at), rather than one that just counts them.
   */
  persistentHeader?: boolean;
  /**
   * A link beside the `header` label, e.g. "View all rooms". Unlike the label it shows whether or not
   * the row scrolls: it goes somewhere, rather than describing the scroll.
   */
  headerAction?: React.ReactNode;
}

/**
 * Per-variant chrome.
 *
 * The icon SIZE is deliberately absent here and resolved at render: `IconSizes` is an import, and
 * reading it at module scope makes this file unimportable for any test that partially mocks
 * `#ui/MaterialIcon.js` without re-exporting it (`PreviewLink.test.tsx` does exactly that).
 */
const ARROW_STYLES = {
  button: {
    strip: 'w-14',
    chip: 'size-9 border bg-background-secondary/90 text-foreground shadow-md hover:bg-background-secondary',
  },
  // Unused by the `header` bar, which lays its own buttons out — kept so the lookup stays total.
  header: { strip: '', chip: '' },
} as const;

/**
 * One item plus the inter-item gap, read from the first two children so any gap is honoured without
 * the caller having to declare it. Falls back to the first child's width for a single-item row.
 */
function measureStride(el: HTMLElement): number {
  const first = el.firstElementChild as HTMLElement | null;
  const second = el.children[1] as HTMLElement | null;
  if (first && second) return second.offsetLeft - first.offsetLeft;
  return first?.offsetWidth ?? 0;
}

export function CarouselRow({
  children,
  className,
  contentClassName,
  label,
  fadeFrom = 'from-background',
  arrows = 'button',
  header,
  persistentHeader = false,
  headerAction,
}: CarouselRowProps) {
  const arrowStyle = ARROW_STYLES[arrows];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 2;
    setCanScrollLeft(el.scrollLeft > threshold);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - threshold);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState]);

  const scrollByItem = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const stride = measureStride(el) || el.clientWidth;
    el.scrollBy({ left: direction === 'left' ? -stride : stride, behavior: 'smooth' });
  };

  if (arrows === 'header') {
    const headerButton = (direction: 'left' | 'right', enabled: boolean) => (
      <button
        type="button"
        onClick={() => scrollByItem(direction)}
        disabled={!enabled}
        aria-label={`Scroll ${label ?? 'row'} ${direction}`}
        // `foreground/10` rather than a surface token: the bar sits on whatever the caller's
        // background is, and in chat that is `background-secondary` — the same colour the previous
        // hover used, so hovering visibly did nothing. A tint of the text colour reads on any surface.
        className="flex size-8 cursor-pointer items-center justify-center rounded-full text-foreground transition-colors hover:bg-foreground/10 active:bg-foreground/15 disabled:pointer-events-none disabled:opacity-30"
      >
        <MaterialIcon icon={direction === 'left' ? 'chevron_left' : 'chevron_right'} size={IconSizes.md} />
      </button>
    );

    // Nothing overflows, so there is nowhere to scroll: the whole bar goes rather than sitting there
    // with dead controls. The label goes with it — "4 recommended hotels" above four visible cards is
    // just counting them for the reader.
    const scrolls = canScrollLeft || canScrollRight;
    // The label goes with the arrows, unless it says something the cards don't (`persistentHeader`).
    const showHeader = scrolls || (persistentHeader && !!header);

    return (
      // `gap-1`, not the `gap-3` the row's other spacing uses. The bar's buttons are 32px tall against
      // a much shorter line of label text, so they contribute their own optical padding beneath the
      // baseline — a 12px box gap on top of that read as a break between the controls and the row they
      // drive, rather than as the pair belonging together.
      <div className={cn('flex flex-col gap-2', className)}>
        {(showHeader || headerAction) && (
          // `relative z-10` keeps the controls above the row's edge fades. Those are absolutely
          // positioned and bleed `-inset-y-4` to cover the cards' hover halos, so they reach 16px
          // above the scroll wrapper — which lands inside this bar now that the gap is 4px. Being
          // positioned, they would otherwise paint over this in-flow bar and slice the bottom off a
          // hovered arrow.
          <div className="relative z-10 flex items-center justify-between gap-3">
            {/* Always rendered alongside the arrows, even when empty, so they stay hard right rather
                than jumping to the left edge for a caller that passes no label. */}
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {showHeader && header}
              {headerAction}
            </div>
            {scrolls && (
              <div className="flex shrink-0 items-center gap-1">
                {headerButton('left', canScrollLeft)}
                {headerButton('right', canScrollRight)}
              </div>
            )}
          </div>
        )}
        {/* The fade is the other half of the affordance. With the arrows lifted into the bar, nothing
            at the row's edge said "there is more" — a card simply stopped mid-width at the boundary,
            which reads as clipping rather than as scrolling. Same edge treatment the image carousels
            use; it just cannot ride on the arrows here, so it gets its own layer. */}
        <div className="relative">
          {/* `p-4 -m-4` is layout-neutral — the padding gives a card's hover halo room to paint INSIDE
              the scroll box, the negative margin takes that room straight back out of the flow, so
              nothing moves. It has to be all FOUR sides. Vertically because `overflow-x: auto` forces
              the vertical axis to clip too (per spec `visible` computes to `auto` when the other axis
              is not visible), and horizontally because the first card's halo reaches past the scroll
              origin at `scrollLeft: 0` and the last card's past the end. The 16px bleed sits inside
              the chat column's own `px-4`, so `ChatMessagesView`'s `overflow-x-hidden` never sees it.

              `scroll-p-3` has to match that padding, and is not cosmetic. A `snap-start` child aligns
              its edge to the snapport, which defaults to the PADDING BOX — but the first card sits
              after `padding-left`, so mandatory snapping settled at `scrollLeft: 12` instead of 0.
              `canScrollLeft` is `scrollLeft > 2`, so the left fade was on permanently even when the
              row was scrolled hard left. Insetting the snapport by the same 16px puts the first card's
              snap position back at 0. */}
          <div
            ref={scrollRef}
            className={cn(
              'flex gap-4 overflow-x-auto p-4 -m-4 scroll-p-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
              contentClassName,
            )}
          >
            {children}
          </div>
          {/* Pinned to `-inset-4`, not `inset-0`. The scroll box's negative margin makes it 16px wider
              than this wrapper on each side, so a fade at the wrapper's edge would leave a 16px strip
              of un-faded card beyond it — the exact sliver the padding was added to expose. */}
          {canScrollLeft && (
            <div
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute -inset-y-4 -left-4 w-12 bg-gradient-to-r to-transparent',
                fadeFrom,
              )}
            />
          )}
          {canScrollRight && (
            <div
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute -inset-y-4 -right-4 w-12 bg-gradient-to-l to-transparent',
                fadeFrom,
              )}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('relative', className)}>
      {/* The scrollbar is hidden, not the scrolling: the arrows and the edge fade already advertise there's
          more, and an overlay bar appearing mid-scroll across the cards is a distraction. Trackpad, touch
          and keyboard scrolling are untouched. Tailwind v4 ships no utility for this, hence the two
          arbitrary properties (Firefox, then WebKit/Blink). */}
      <div
        ref={scrollRef}
        className={cn(
          'flex gap-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          contentClassName,
        )}
      >
        {children}
      </div>

      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scrollByItem('left')}
          aria-label={label ? `Scroll ${label} left` : 'Scroll left'}
          className={cn(
            'absolute inset-y-0 left-0 z-10 flex cursor-pointer items-center justify-start bg-gradient-to-r to-transparent',
            arrowStyle.strip,
            fadeFrom,
          )}
        >
          <span
            className={cn(
              'flex items-center justify-center rounded-full backdrop-blur-sm transition-colors',
              arrowStyle.chip,
            )}
          >
            <MaterialIcon icon="chevron_left" size={IconSizes.md} />
          </span>
        </button>
      )}

      {canScrollRight && (
        <button
          type="button"
          onClick={() => scrollByItem('right')}
          aria-label={label ? `Scroll ${label} right` : 'Scroll right'}
          className={cn(
            'absolute inset-y-0 right-0 z-10 flex cursor-pointer items-center justify-end bg-gradient-to-l to-transparent',
            arrowStyle.strip,
            fadeFrom,
          )}
        >
          <span
            className={cn(
              'flex items-center justify-center rounded-full backdrop-blur-sm transition-colors',
              arrowStyle.chip,
            )}
          >
            <MaterialIcon icon="chevron_right" size={IconSizes.md} />
          </span>
        </button>
      )}
    </div>
  );
}
